import {
  useState,
  useMemo,
  useCallback,
  useEffect
} from 'react'

import jsPDF from "jspdf";
import { LoadingContent } from '../../../../../components/SharedComponents/sharedComponents.index'
import { LOADING_CONTENT_TYPE } from '../../../../../components/SharedComponents/LoadingContent/loading.content.constants'

const RANK_OPTIONS = [
  { value: 'units', label: 'Venda Total de Peças' },
  { value: 'customers', label: 'Número de Clientes Atendidos' },
  { value: 'revenue', label: 'Faturamento' },
];

const COLOR_PALETTE = ['#d4a853', '#7b9ecf', '#5a8a5c', '#e8773a', '#6b7280', '#1d4ed8', '#be185d', '#065f46'];
const SIZE_PALETTE = [[232,119,58], [185,185,185], [196,196,196], [224,224,224]];

const parsePipeList = (raw) => {
  if (!raw) return [];
  return raw
    .split('|')
    .map(entry => {
      const [label, pct] = entry.split(':').map(s => s?.trim());
      return { label, pct: Number(pct) };
    })
    .filter(item => item.label && !Number.isNaN(item.pct));
};

const buildPdf = ({ filters, products, seasonOptions, onClose, cardsPerPage }) => {
  const hexToRgb = hex => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex ?? '');
    return m ? [parseInt(m[1],16), parseInt(m[2],16), parseInt(m[3],16)] : [200,200,200];
  };
  const fmtNum = n => n != null ? n.toLocaleString('pt-BR') : '—';
  const fmtK   = n => n != null ? `R$ ${(n/1000).toFixed(0)}k` : '—';
  const fmtPct = n => {
    if (n == null) return '—';
    const pct = n > 1 ? n : n * 100;
    return `${pct.toFixed(0)}%`;
  };
  const fmtR$  = n => n != null ? `R$ ${Number(n).toFixed(2)}` : '—';
  const clip   = (s, max) => s && s.length > max ? s.slice(0,max)+'…' : (s ?? '—');

  const sorted = [...products]
    .filter(p => {
      if (filters.collection && p?.season?.seasonId !== filters.collection) return false;
      if (filters.brand?.length && !filters.brand.includes(p.levels?.level1))    return false;
      if (filters.gender?.length && !filters.gender.includes(p.levels?.level2))   return false;
      if (filters.segment?.length && !filters.segment.includes(p.levels?.level3))  return false;
      return true;
    })
    .sort((a, b) => {
      const { financialData: fa, buyers: ba } = a || {};
      const { financialData: fb, buyers: bb } = b || {};

      if (filters.rankBy === 'revenue')   return (fb?.revenue ?? 0) - (fa?.revenue ?? 0);
      if (filters.rankBy === 'customers') return (bb?.shares?.buyingCustomersShare ?? 0) - (ba?.shares?.buyingCustomersShare ?? 0);
      return (fb?.unitsSold ?? 0) - (fa?.unitsSold ?? 0);
    });

  const TOTAL = products.length;
  const colLabel = seasonOptions?.find(s => s.value === filters.collection)?.label ?? filters.collection ?? '';

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const PW = 297, PH = 210;
  const COLS = 2;
  const ROWS = cardsPerPage === 2 ? 1 : 2;
  const M = 7, HH = 12, GAP = 3;
  const CW = (PW - 2*M - GAP) / COLS;
  const CH = (PH - HH - 2 - GAP) / ROWS;
  const totalPages = Math.ceil(sorted.length / (COLS * ROWS));

  const RIGHT_COL_NATURAL_H = 103;
  const availableRightH = CH - 2.5 - 3;
  const RSCALE = Math.min(1, availableRightH / RIGHT_COL_NATURAL_H);

  const drawPageHeader = pg => {
    doc.setFillColor(232,119,58);
    doc.rect(0, 0, PW, HH, 'F');
    doc.setTextColor(255,255,255);
    doc.setFont('helvetica','bold'); doc.setFontSize(9);
    doc.text('Performance Comercial dos Produtos', M, 8.5);

    const filterStr = [
      colLabel && `Coleção: ${colLabel}`,
      filters.brand?.length && `Marca: ${filters.brand.join(', ')}`,
      filters.gender?.length && `Gênero: ${filters.gender.join(', ')}`,
      filters.segment?.length && `Segmento: ${filters.segment.join(', ')}`,
      `Regional: ${filters.regional || 'todas'}`,
      `Representante: ${filters.representative || 'todos'}`,
      `Rank: ${filters.rankBy === 'revenue' ? 'Faturamento' : filters.rankBy === 'customers' ? 'Adesão Clientes' : 'Venda Total de Peças'}`,
    ].filter(Boolean).join('  |  ');
    doc.setFont('helvetica','normal'); doc.setFontSize(5.5);
    doc.text(filterStr, PW/2, 8.5, { align:'center' });
    doc.setFontSize(6.5);
    doc.text(`Página ${pg} de ${totalPages}`, PW-M, 8.5, { align:'right' });
  };

  const secHeader = (x, y, text) => {
    doc.setFillColor(232,119,58);
    doc.rect(x, y+0.5, 2, 5, 'F');
    doc.setFont('helvetica','bold'); doc.setFontSize(6); doc.setTextColor(25);
    doc.text(text, x+3.5, y+4.5);
  };

  const drawCard = (p, rank, cx, cy) => {
    const { financialData } = p || {};
    const { buyers } = p || {};
    const { rankPositions } = p || {};
    const { salesByProductFeatures } = p || {};

    const { salesByProductColor, salesByProductSize } = salesByProductFeatures || {};
    const { revenue, averagePrice, unitsSold, profitShare } = financialData || {};
    const { shares } = buyers || {};
    const { buyingCustomersShare, retailCustomersShare, wholesaleCustomersShare } = shares || {};
    const { purchaseAffinity, totalSales, revenue: revenueRankPosition } = rankPositions || {};

    const colorData = parsePipeList(salesByProductColor).map((item, i) => ({
      ...item,
      hex: COLOR_PALETTE[i % COLOR_PALETTE.length],
    }));
    const gradeData = parsePipeList(salesByProductSize);

    const PAD = 2.5;
    const LW = CW * 0.555;
    const RW = CW - LW - PAD*2 - 0.5;
    const LX = cx + PAD;
    const RX = cx + LW + PAD + 0.5;

    doc.setFillColor(255,255,255);
    doc.setDrawColor(210,210,210); doc.setLineWidth(0.25);
    doc.roundedRect(cx, cy, CW, CH, 2, 2, 'FD');
    doc.setLineWidth(0.2);

    const FH = 8.8, FG = 1.2;
    const FW = (LW - PAD - 1.5) / 2;
    const REFS = [
      ['REFERÊNCIA', p.name ?? '—'],
      ['MARCA', p.levels?.level1 ?? '—'],
      ['COLEÇÃO', colLabel || '—'],
      ['SEGMENTO', p.levels?.level3 ?? '—'],
      ['REGIONAL', filters.regional || 'Todas'],
      ['REPRESENTANTE', filters.representative || 'Todos'],
    ];
    REFS.forEach(([lbl, val], i) => {
      const fc = i%2, fr = Math.floor(i/2);
      const fx = LX + fc*(FW+1.5);
      const fy = cy + PAD + fr*(FH+FG);
      doc.setFillColor(255,255,255); doc.setDrawColor(228,228,228);
      doc.roundedRect(fx, fy, FW, FH, 1, 1, 'FD');
      doc.setFillColor(232,119,58);
      doc.circle(fx+2.2, fy+2.2, 1.1, 'F');
      doc.setFont('helvetica','normal'); doc.setFontSize(4.5); doc.setTextColor(155);
      doc.text(lbl, fx+5, fy+2.8);
      doc.setFont('helvetica','bold'); doc.setFontSize(6.5); doc.setTextColor(25);
      doc.text(clip(val,14), fx+1.8, fy+7.2);
    });

    const refBotY = cy + PAD + 3*(FH+FG) - FG + 1;
    doc.setDrawColor(235,235,235);
    doc.line(LX, refBotY, LX+LW-PAD, refBotY);

    // seção da imagem
    const imgSecY = refBotY + 1.5;
    secHeader(LX, imgSecY, 'IMAGEM TAG');
    const imgY = imgSecY + 7;
    const imgH = CH - (imgY - cy) - PAD - 4;
    const imgW = LW - PAD;

    const { tag, mimeType } = p.img || {};
    let drawnImgH = imgH;

    if (tag) {
      const normalizedMime = (mimeType || 'jpeg').replace('image/', '').toLowerCase();
      const jsPdfFormat = normalizedMime === 'jpg' ? 'JPEG' : normalizedMime.toUpperCase();
      const dataUri = `data:image/${normalizedMime};base64,${tag}`;
      try {
        const { width: natW, height: natH } = doc.getImageProperties(dataUri);
        const scale = Math.min(imgW / natW, imgH / natH);
        const drawW = natW * scale;
        const drawH = natH * scale;
        const drawX = LX + (imgW - drawW) / 2;
        doc.addImage(dataUri, jsPdfFormat, drawX, imgY, drawW, drawH, undefined, 'FAST');
        drawnImgH = drawH;
      } catch (e) {
        doc.setFillColor(237,241,246);
        doc.roundedRect(LX, imgY, imgW, imgH, 1.5, 1.5, 'F');
      }
    } else {
      doc.setFillColor(237,241,246);
      doc.roundedRect(LX, imgY, imgW, imgH, 1.5, 1.5, 'F');
      doc.setFont('helvetica','bold'); doc.setFontSize(8); doc.setTextColor(88);
      doc.text(p.levels?.level8 ?? '', LX+imgW/2, imgY+imgH/2-2, { align:'center' });
      doc.setFont('helvetica','normal'); doc.setFontSize(6.5); doc.setTextColor(135);
      doc.text(p.levels?.level1 ?? '', LX+imgW/2, imgY+imgH/2+5, { align:'center' });
    }

    const attrStr = [p.levels?.level5, p.levels?.level6, p.levels?.level7].filter(Boolean).join(' · ');
    doc.setFont('helvetica','normal'); doc.setFontSize(4.5); doc.setTextColor(155);
    doc.text(attrStr, LX, imgY + drawnImgH + 4);

    let ry = cy + PAD;

    secHeader(RX, ry, 'RANKING');
    ry += 7 * RSCALE;
    const rkItems = [
      ['VENDA EM PEÇAS',  `#${totalSales ?? rank+1}`],
      ['FATURAMENTO',     `#${revenueRankPosition ?? rank+1}`],
      ['ADESÃO CLIENTES', `#${purchaseAffinity ?? '—'}`],
    ];
    const RKW = RW/3 - 0.8;
    const RK_H = 14 * RSCALE;
    rkItems.forEach(([lbl, val], i) => {
      const rkx = RX + i*(RKW+1.2);
      doc.setFillColor(250,250,250); doc.setDrawColor(235,235,235);
      doc.roundedRect(rkx, ry, RKW, RK_H, 1.2, 1.2, 'FD');
      doc.setFont('helvetica','normal'); doc.setFontSize(4); doc.setTextColor(148);
      doc.text(lbl, rkx+RKW/2, ry+3.5, { align:'center' });
      doc.setFont('helvetica','bold'); doc.setFontSize(9.5); doc.setTextColor(25);
      doc.text(val, rkx+2, ry+RK_H-3);
      const vw = doc.getTextWidth(val);
      doc.setFont('helvetica','normal'); doc.setFontSize(4.5); doc.setTextColor(155);
      doc.text(`/${TOTAL}`, rkx+2+vw+0.5, ry+RK_H-3);
    });
    ry += 16 * RSCALE;

    doc.setDrawColor(235,235,235); doc.line(RX, ry, RX+RW, ry); ry += 1.5 * RSCALE;

    secHeader(RX, ry, 'DESEMPENHO');
    ry += 7 * RSCALE;
    const DESP = [
      ['VENDA TOTAL', fmtNum(unitsSold), true ],
      ['ATACADO', fmtPct(wholesaleCustomersShare), false],
      ['VAREJO', fmtPct(retailCustomersShare), false],
      ['ADESÃO CLI.', fmtPct(buyingCustomersShare), false],
      ['PREÇO MÉD.', fmtR$(averagePrice), false],
      ['FATURAMENTO', fmtK(revenue), true ],
    ];
    const DCW = RW/2 - 0.5, DRH = 7.5 * RSCALE;
    DESP.forEach(([lbl, val, hi], i) => {
      const dc = i%2, dr = Math.floor(i/2);
      const dx = RX + dc*(DCW+1);
      const dy = ry + dr*DRH;
      doc.setFillColor(hi?255:251, hi?248:251, hi?245:251);
      doc.setDrawColor(hi?244:237, hi?228:237, hi?215:237);
      doc.roundedRect(dx, dy, DCW, DRH-0.5, 1, 1, 'FD');
      doc.setFont('helvetica','normal'); doc.setFontSize(4.5); doc.setTextColor(152);
      doc.text(lbl, dx+2, dy+2.8);
      doc.setFont('helvetica','bold'); doc.setFontSize(6.5);
      doc.setTextColor(hi?232:25, hi?119:25, hi?58:25);
      doc.text(val, dx+2, dy+6.5);
    });
    ry += 3*DRH + 1 * RSCALE;

    doc.setFont('helvetica','normal'); doc.setFontSize(4.5); doc.setTextColor(152);
    doc.text('Lucro Líquido', RX, ry+1.2*RSCALE);
    doc.setFillColor(222,222,222);
    doc.roundedRect(RX, ry+3*RSCALE, RW-9, 2.5, 0.8, 0.8, 'F');
    doc.setFillColor(22,163,74);
    const profShare = profitShare ?? 0;
    const profPctValue = profShare > 1 ? profShare : profShare * 100;
    doc.roundedRect(RX, ry+3*RSCALE, Math.max(0.4,(RW-9)*Math.min(profPctValue/100,1)), 2.5, 0.8, 0.8, 'F');
    doc.setFont('helvetica','bold'); doc.setFontSize(5.5); doc.setTextColor(22,163,74);
    doc.text(fmtPct(profShare), RX+RW-7, ry+5.2*RSCALE);
    ry += 8 * RSCALE;

    doc.setDrawColor(235,235,235); doc.line(RX, ry, RX+RW, ry); ry += 1.5 * RSCALE;

    secHeader(RX, ry, 'VENDA POR COR');
    ry += 7 * RSCALE;
    colorData.slice(0,3).forEach((c, i) => {
      const cy2 = ry + i*5*RSCALE;
      const [r,g,b] = hexToRgb(c.hex);
      doc.setFillColor(r,g,b); doc.circle(RX+2, cy2+1.5, 1.7, 'F');
      doc.setFont('helvetica','normal'); doc.setFontSize(5); doc.setTextColor(75);
      doc.text(clip(c.label ?? `Variante ${i+1}`, 11), RX+5.5, cy2+2.5);
      const BW = RW-23, BX = RX+RW-BW-4;
      doc.setFillColor(222,222,222);
      doc.roundedRect(BX, cy2, BW, 2.5, 0.5, 0.5, 'F');
      doc.setFillColor(r,g,b);
      doc.roundedRect(BX, cy2, Math.max(0.4, BW*(c.pct/100)), 2.5, 0.5, 0.5, 'F');
      doc.setFontSize(5); doc.setTextColor(100);
      doc.text(`${c.pct}%`, RX+RW-2.5, cy2+2.3, { align:'right' });
    });
    ry += colorData.length*5*RSCALE + 1.5*RSCALE;

    doc.setDrawColor(235,235,235); doc.line(RX, ry, RX+RW, ry); ry += 1.5 * RSCALE;

    secHeader(RX, ry, 'VENDA POR GRADE');
    ry += 7 * RSCALE;
    const GBW = RW - 1;
    let gx = RX;
    gradeData.forEach((g, i) => {
      const gw = Math.max(0.4, GBW*(g.pct??0)/100);
      const [r,gv,b] = SIZE_PALETTE[i % SIZE_PALETTE.length];
      doc.setFillColor(r,gv,b);
      doc.rect(gx, ry, gw, 5, 'F');
      if (gw > 10) {
        doc.setFont('helvetica','bold'); doc.setFontSize(5.5); doc.setTextColor(255,255,255);
        doc.text(`${g.pct??0}%`, gx+gw/2, ry+3.5, { align:'center' });
      }
      gx += gw;
    });
    ry += 6.5 * RSCALE;
    gradeData.forEach((g, i) => {
      const [r,gv,b] = SIZE_PALETTE[i % SIZE_PALETTE.length];
      doc.setFillColor(r,gv,b);
      doc.circle(RX+2+i*18, ry+1.5, 1.7, 'F');
      doc.setFont('helvetica','normal'); doc.setFontSize(5); doc.setTextColor(75);
      doc.text(g.label ?? `Grade ${i}`, RX+5+i*18, ry+2.5);
    });
  };

  const PER_PAGE = COLS * ROWS;
  sorted.forEach((p, idx) => {
    const pg  = Math.floor(idx / PER_PAGE);
    const pos = idx % PER_PAGE;
    if (pos === 0) { if (pg > 0) doc.addPage(); drawPageHeader(pg+1); }
    const col = pos % COLS;
    const row = Math.floor(pos / COLS);
    const cx = M + col*(CW+GAP);
    const cy = HH + 2 + row*(CH+GAP);
    drawCard(p, idx, cx, cy);
  });

  doc.save('performance-produtos.pdf');
  onClose();
};

const SelectField = ({ actions, ctx, uiState  }) => {

  const { onToggle, onChange, onToggleValue } = actions || {};

  const { 
    label,
    fieldKey,
    options = [],
    value,
    multi
   } = ctx || {}

   const { isOpen } = uiState || {}

  const selectedValues = multi ? (value ?? []) : value;
  const selectedLabel = multi
    ? (selectedValues.length
        ? selectedValues.map(v => options.find(o => (o.value ?? o) === v)?.label ?? v).join(', ')
        : null)
    : (options.find(o => (o.value ?? o) === value)?.label ?? (typeof value === 'string' ? value : null));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: '12px', color: '#555', fontWeight: '500' }}>{label}:</label>
      <div
        style={{ position: 'relative' }}
        tabIndex={-1}
        onBlur={e => {
          if (!e.currentTarget.contains(e.relatedTarget)) onToggle(null);
        }}
      >
        <button
          onClick={() => onToggle(isOpen ? null : fieldKey)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
            padding: '9px 12px', borderRadius: '8px',
            border: isOpen ? '1.5px solid #E8773A' : '1.5px solid #e0e0e0',
            fontSize: '13px', color: selectedLabel ? '#1a1a1a' : '#aaa',
            backgroundColor: '#fff', cursor: 'pointer', outline: 'none', textAlign: 'left',
            transition: 'border-color 0.15s',
          }}
        >
          <span>{selectedLabel ?? 'Selecionar'}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={isOpen ? '#E8773A' : '#bbb'} strokeWidth="2.5"
            style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>

        {isOpen && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
            backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 9999,
            maxHeight: 200, overflowY: 'auto',
          }}>
            {options.map((opt, index) => {
              const val = opt.value ?? opt;
              const lbl = opt.label ?? opt;
              const isActive = multi ? selectedValues.includes(val) : value === val;
              return (
                <button key={val} onClick={() => {
                  if (multi) { onToggleValue(fieldKey, val); }
                  else { onChange(fieldKey, val); onToggle(null); }
                }} style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '9px 14px', background: isActive ? '#fff6f0' : 'none',
                  border: 'none', cursor: 'pointer', fontSize: '13px',
                  color: isActive ? '#E8773A' : '#333',
                  fontWeight: isActive ? '700' : '400', textAlign: 'left',
                  borderBottom: index < options.length - 1 ? '1px solid #f5f5f5' : 'none',
                }}>
                  <span>{lbl}</span>
                  {isActive && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#E8773A" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export const PdfModal = ({ actions, ctx, uiState }) => {

  const [openField, setOpenField] = useState(null);
  const [pendingExportIds, setPendingExportIds] = useState(null);

  const [filters, setFilters] = useState(() => ({
    collection: ctx?.productReportSeason,
    brand: ctx?.activeFilters?.Marca ?? [],
    gender: ctx?.activeFilters?.Genero ?? [],
    segment: ctx?.activeFilters?.Grade ?? [],
    rankBy: 'units',
  }));
  const [cardsPerPage, setCardsPerPage] = useState(4);

  const { 
    onClose, 
    setPdfExportProductIds, 
    setPdfExportSeasonId,
    onExportComplete
  } = actions || {};

  const { 
    seasonOptions, 
    filterOptions,
    productReportSeason,
    products = [],
    pdfExportImages
  } = ctx || {};

  const { isLoadingPdfExportImages } = uiState || {};

  const setFilter = (key, val) => setFilters(data => ({ ...data, [key]: val }));
  const toggleMultiFilter = (key, val) => setFilters(dataToFilter => {
    const arr = dataToFilter[key] ?? [];
    return { ...dataToFilter, [key]: arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val] };
  });

  const brandOptions = useMemo(
    () => Object.values(filterOptions?.hierarquia ?? {})[0]?.map(option => option.label) ?? [], 
    [filterOptions]
  );

  const genderOptions = useMemo(
    () => Object.values(filterOptions?.hierarquia ?? {})[1]?.map(option => option.label) ?? [], 
    [filterOptions]
  );

  const segmentOptions = useMemo(
    () => Object.values(filterOptions?.hierarquia ?? {})[2]?.map(option => option.label) ?? [], 
    [filterOptions]
  );

  const regionalOptions = useMemo(() => {
    const regionalSet = new Set();
    products.forEach(p => {
      const regional = p?.buyers?.regional;
      if (regional) regionalSet.add(regional);
    });
    return Array.from(regionalSet).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [products]);

  const representativeOptions = useMemo(() => {
    const representativeSet = new Set();
    products.forEach(p => {
      const representative = p?.buyers?.representative;
      if (representative) representativeSet.add(representative);
    });
    return Array.from(representativeSet).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [products]);

  const filteredCount = useMemo(() => {
    return products.filter(product => {
      if (filters.collection && product?.season?.seasonId !== filters.collection) return false;
      if (filters.brand?.length && !filters.brand.includes(product.levels?.level1)) return false;
      if (filters.gender?.length && !filters.gender.includes(product.levels?.level2)) return false;
      if (filters.segment?.length && !filters.segment.includes(product.levels?.level3)) return false;
      return true;
    }).length;
  }, [products, filters.collection, filters.brand, filters.gender, filters.segment]);

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      if (filters.collection && product?.season?.seasonId !== filters.collection) return false;
      if (filters.brand?.length && !filters.brand.includes(product.levels?.level1)) return false;
      if (filters.gender?.length && !filters.gender.includes(product.levels?.level2)) return false;
      if (filters.segment?.length && !filters.segment.includes(product.levels?.level3)) return false;
      return true;
    });
  }, [products, filters.collection, filters.brand, filters.gender, filters.segment]);

  const canExport = !!filters.collection;

  const fields = useMemo(() => [
    { 
      label: 'Coleção', 
      fieldKey: 'collection', 
      options: seasonOptions ?? []
    },
    { 
      label: 'Marca', 
      fieldKey: 'brand', 
      options: brandOptions,
      multi: true
    },
    { 
      label: 'Gênero',
      fieldKey: 'gender',
      options: genderOptions,
      multi: true
    },
    { 
      label: 'Segmento',
      fieldKey: 'segment',
      options: segmentOptions,
      multi: true
    },
    { 
      label: 'Regional',
      fieldKey: 'regional',
      options: regionalOptions
    },
    { 
      label: 'Representante',
      fieldKey: 'representative',
      options: representativeOptions
    },
  ], [seasonOptions, brandOptions, genderOptions, segmentOptions, representativeOptions, regionalOptions]);

  const handleExport = useCallback(() => {
    const ids = filteredProducts.map(p => p.productId).filter(Boolean);
    setPendingExportIds(ids);
    setPdfExportProductIds?.(ids);
    setPdfExportSeasonId?.(filters.collection);
  }, [filteredProducts, filters.collection, setPdfExportProductIds, setPdfExportSeasonId]);

  useEffect(() => {
    if (!pendingExportIds) return;
    if (isLoadingPdfExportImages) return;
    if (!pdfExportImages) return;

    const imageByProductId = new Map(pdfExportImages.map(img => [img.productId, img]));

    const productsWithImages = products.map(p => {
      const img = imageByProductId.get(p.productId);
      return img ? { ...p, img: { tag: img.tag, mimeType: img.mimeType } } : p;
    });

    const missingCount = productsWithImages.filter(p => !p.img?.tag).length;

    buildPdf({ filters, products: productsWithImages, seasonOptions, onClose, cardsPerPage });

    if (missingCount > 0) onExportComplete?.({ missingCount, total: productsWithImages.length });

    setPendingExportIds(null);
  }, [
    pendingExportIds, 
    isLoadingPdfExportImages, 
    pdfExportImages, 
    products, 
    filters, 
    seasonOptions, 
    onClose, 
    cardsPerPage,
    onExportComplete
  ]);

  if (isLoadingPdfExportImages) 
    return <LoadingContent label={"Carregando dados..."} type={LOADING_CONTENT_TYPE.API_PAGE_DATA}/>

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)' }} />
      <div style={{
        position: 'relative', backgroundColor: '#fff', borderRadius: '16px',
        padding: '24px 28px', width: 540, boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>

        {/* Header modal */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: '#E8773A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a1a' }}>Gerar PDF</div>
              <div style={{ fontSize: '12px', color: '#aaa' }}>Configure os filtros e o layout da exportação</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: '22px', lineHeight: 1 }}>×</button>
        </div>

        {/* Campos */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px', marginBottom: 18 }}>
          {fields.map(({ label, fieldKey, options, multi }) => (
            <SelectField
              actions={{ onToggle: setOpenField, onChange: setFilter, onToggleValue: toggleMultiFilter }}
              ctx={{
                label: label,
                fieldKey: fieldKey,
                options: options,
                value: filters[fieldKey],
                multi: multi
              }}
              uiState={{ isOpen: openField === fieldKey }}
              key={fieldKey}
            />
          ))}
        </div>

        {/* Rankear por */}
        <div style={{ borderBlockStart: "1px solid rgb(230, 230, 230)", marginBlockStart: '1rem' }}>
          <p style={{ fontSize: '12px', color: 'rgb(51,51,51)', fontWeight: '500', marginBottom: 10 }}>
            Rankear por:
          </p>
          <div style={{ display: 'flex', gap: '0.6rem' }}>
            {RANK_OPTIONS.map(opt => (
              <label
                key={opt.value}
                onClick={() => setFilter('rankBy', opt.value)}
                style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: '13px', color: '#444', whiteSpace: 'nowrap' }}
              >
                <div
                  style={{
                    width: 16, height: 16, borderRadius: '50%', cursor: 'pointer', flexShrink: 0,
                    border: `2px solid ${filters.rankBy === opt.value ? '#E8773A' : '#ddd'}`,
                    backgroundColor: filters.rankBy === opt.value ? '#E8773A' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s',
                  }}
                >
                  {filters.rankBy === opt.value && <div style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#fff' }} />}
                </div>
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        {/* Número de cards por página */}
        <div style={{ borderBlockStart: "1px solid rgb(230, 230, 230)", marginBlockStart: '1rem' }}>
          <p style={{ fontSize: '12px', color: 'rgb(51,51,51)', fontWeight: '500', marginBottom: 10 }}>
            Cards por página:
          </p>

          <div style={{ 
            display: 'flex', backgroundColor: "rgb(240,240,240", borderRadius: '12px',
            alignItems: "center", borderColor: "rgb(230,230,230", boxSizing: "border-box",
            padding: '4px', gap: '4px', inlineSize: 'fit-content', flexDirection: 'row',
            marginBlockEnd: "1rem"
          }}>
            <button
              onClick={() => setCardsPerPage(2)}
              style={{
              alignItems: 'center', appearence: 'button',
              backgroundColor: cardsPerPage === 2 ? 'rgb(237, 112, 29)' : 'transparent',
              borderColor: 'rgb(230,230,230)', borderRadius: '10px',
              boxSizing: 'border-box', color: cardsPerPage === 2 ? 'rgb(255,255,255)' : 'rgb(150,150,150)',
              gap: '8px', display: 'flex', fontSize: '14px',
              fontWeight: 600, padding: '8px 16px', cursor: 'pointer', border: 'none'
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <line x1="4" y1="7" x2="20" y2="7"/>
                <line x1="4" y1="12" x2="20" y2="12"/>
              </svg>
                {"2 cards por página"}
            </button>

            <button
              onClick={() => setCardsPerPage(4)}
              style={{
              alignItems: 'center', appearence: 'button',
              backgroundColor: cardsPerPage === 4 ? 'rgb(237, 112, 29)' : 'transparent',
              borderColor: 'rgb(230,230,230)', borderRadius: '10px',
              boxSizing: 'border-box', color: cardsPerPage === 4 ? 'rgb(255,255,255)' : 'rgb(150,150,150)',
              gap: '8px', display: 'flex', fontSize: '14px',
              fontWeight: 600, padding: '8px 16px', cursor: 'pointer', border: 'none'
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="7.5" cy="7.5" r="3.5"/>
                <circle cx="16.5" cy="7.5" r="3.5"/>
                <circle cx="7.5" cy="16.5" r="3.5"/>
                <circle cx="16.5" cy="16.5" r="3.5"/>
              </svg>
                {"4 cards por página"}
            </button>            
           </div> 
          
        </div>    

        {/* Botões */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '10px 20px', borderRadius: '8px', border: '1.5px solid #e0e0e0', backgroundColor: '#fff', color: '#666', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
            Cancelar
          </button>
          <button
            onClick={handleExport}
            disabled={!canExport || !!pendingExportIds}
            style={{
              padding: '10px 20px', borderRadius: '8px', border: 'none',
              backgroundColor: canExport ? '#E8773A' : '#e0e0e0',
              color: canExport ? '#fff' : '#aaa',
              fontSize: '13px', fontWeight: '700',
              cursor: canExport ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', gap: 8,
              transition: 'all 0.15s',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            {pendingExportIds ? 'Gerando PDF...' : `Exportar PDF (${filteredCount})`}
          </button>
        </div>
      </div>
    </div>
  );
};